/**
 * Telling "the connection failed" apart from "the service said no".
 *
 * A request that dies before any HTTP response rejects with a browser-level
 * `TypeError`, whose message is the only thing that identifies it — and each
 * engine words it differently. A refusal (404 for a bad model id, 429 for a
 * rate limit, 403 for a key restriction) comes back as a *response* and never
 * matches these, which is the distinction worth surfacing to a tech: one means
 * "move to where you have signal and retry", the other does not.
 *
 * The Firebase AI SDK re-wraps the browser error into its own
 * `AI: Error fetching from <url>: Failed to fetch (AI/error)`, so the check is
 * on the message text rather than `instanceof TypeError`.
 */

const NETWORK_FAILURE = /failed to fetch|networkerror when attempting to fetch|network request failed|load failed|err_(internet_disconnected|network_changed|connection_(closed|reset|timed_out))/i;

/** True when the request never reached the server, or its answer never came back. */
export const isNetworkError = (err: unknown): boolean => {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return NETWORK_FAILURE.test(message);
};

/**
 * The browser's own view of connectivity. `false` is reliable ("the radio is
 * off"); `true` only means an interface exists, which is why a failed request
 * still counts as a network failure on a phone that claims to be online.
 */
export const isOnline = (): boolean => typeof navigator === 'undefined' ? true : navigator.onLine !== false;
